import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import useCurrentUser from "hooks/useCurrentUser";
import useTags from "hooks/useTags";
import { uniqBy } from "lodash";
import { useState } from "react";
import { Tag } from "types/tag";
import { userHasPermission } from "utils/userPermissions";

type TagsProps = {
  tags: TagOptionType[];
  onChange: any;
  disabled?: boolean;
  size?: any;
};

interface TagOptionType extends Tag {
  inputValue?: string;
}

export default function Tags({ tags, onChange, disabled, size }: TagsProps) {
  const { tags: defaultTags } = useTags();
  const filter = createFilterOptions<TagOptionType>();
  const [tempNewTag, setTempNewTag] = useState("");
  const { currentUserPermissions } = useCurrentUser();
  const hasPermission = userHasPermission("warehouse_manager", currentUserPermissions);

  return (
    <Autocomplete
      disabled={disabled}
      multiple
      freeSolo
      disableClearable
      filterSelectedOptions
      inputValue={tempNewTag}
      value={tags}
      slotProps={{
        chip: {
          color: "primary",
        },
      }}
      onChange={(e, newValue) => {
        onChange(
          uniqBy(
            newValue.map((t) => {
              const o = t as TagOptionType;
              if (typeof o === "string") {
                return {
                  id: null,
                  name: o,
                };
              } else if (o.inputValue && typeof o.inputValue === "string") {
                return {
                  id: null,
                  name: o.inputValue,
                };
              }
              return o;
            }),
            "name",
          ),
        );
        setTempNewTag("");
      }}
      options={defaultTags as TagOptionType[]}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        const { inputValue } = params;
        const isExisting = options.some((option) => inputValue === option.name);
        if (hasPermission && inputValue !== "" && !isExisting) {
          filtered.push({
            inputValue,
            id: 0,
            name: `Create brand new tag: "${inputValue}"`,
          });
        }

        return filtered;
      }}
      isOptionEqualToValue={(t1, t2) => t1.name === t2.name}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.inputValue) {
          return option.name;
        }
        return option.name;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{ width: "100%" }}
          size={size}
          value={tempNewTag}
          onChange={(e) => setTempNewTag(e.target.value)}
          onKeyDown={(e) => {
            const isExisting = defaultTags.some((option) => tempNewTag === option.name);
            if (e.key === "Enter" && tempNewTag) {
              if (!isExisting) {
                e.preventDefault();
                e.stopPropagation();
              } else {
                setTempNewTag("");
              }
            }
          }}
        />
      )}
    />
  );
}
